// src/core/messages.ts
import type { ChatMessage, MessageAuthor, MessageStatus, MessageType } from '../types/public';
import type { RawIncomingMessage, RawFilePayload } from '../types/events';

export function mapIncomingMessage(raw: RawIncomingMessage): ChatMessage | null {
  if (!raw) return null;
  const status: MessageStatus = 'delivered';
  const direction: 'in' | 'out' = inferDirection(raw);
  const sentAt = raw.sent_at ? new Date(raw.sent_at as string | number | Date).getTime() : Date.now();
  const author = buildAuthor(raw);
  const rawTypeStr = typeof raw.type === 'string' ? raw.type : undefined;
  const type = mapType(rawTypeStr);
  const fileIdSrc =
    raw.file?.fileId != null
      ? Number(raw.file.fileId)
      : raw.file_id != null
        ? Number(raw.file_id)
        : undefined;
  const fileUrl = raw.file?.url ?? raw.url;
  const fileName = raw.file?.name;
  const fileMime = raw.file?.type;
  const hasFile = Boolean(fileUrl || fileName || (fileIdSrc != null && Number.isFinite(fileIdSrc)));
  const inferredType = fileMime ? mapType(fileMime) : type;
  // Если есть файл, но mime не опознан (например xlsx, docx) — mapType вернёт 'text'.
  // Это валидный документ, не пустое текстовое сообщение → форсим 'document'.
  const finalType: MessageType = hasFile && inferredType === 'text' ? 'document' : inferredType;

  return {
    id: String(raw.id ?? raw.external_id ?? generateLocalId()),
    externalId: raw.external_id,
    direction,
    type: finalType,
    text: raw.text ?? '',
    sentAt,
    status,
    author,
    fileId: Number.isFinite(fileIdSrc) ? fileIdSrc : undefined,
    fileUrl,
    fileName,
    options: direction === 'in' ? extractOptions(raw.params) : undefined,
  };
}

function extractOptions(params: unknown): string[] | undefined {
  if (!params || typeof params !== 'object') return undefined;
  const raw = (params as { option_buttons?: unknown }).option_buttons;
  if (!Array.isArray(raw)) return undefined;
  const options = raw.filter((o): o is string => typeof o === 'string' && o.trim() !== '');
  return options.length ? options : undefined;
}

/**
 * Кнопки активны только у последнего входящего сообщения с вариантами,
 * пока клиент на него не ответил: после любого исходящего сообщения вопрос
 * считается закрытым — как в Telegram, где клавиатура снимается после ответа.
 */
export function findAnswerableMessageId(list: ChatMessage[]): string | undefined {
  for (let i = list.length - 1; i >= 0; i--) {
    const m = list[i];
    if (m.type === 'system') continue;
    if (m.direction === 'out') return undefined;
    if (m.options?.length) return m.id;
  }
  return undefined;
}

export function mapFilePayload(raw: RawFilePayload): ChatMessage {
  const id = String(raw.id ?? raw.fileId ?? Date.now());
  // 'file' event — это всегда файл по семантике; если mime не опознан, fallback на 'document'.
  const t = mapType(raw.type);
  const type: MessageType = t === 'text' ? 'document' : t;
  return {
    id,
    externalId: id,
    type,
    direction: 'in',
    // Caption (подпись оператора к фото/файлу): бэк cp-core кладёт её в `text`
    // payload'а file-event'а. См. web-client.service.ts send_photo/document/...
    text: raw.text,
    fileName: raw.name,
    fileUrl: raw.url,
    fileId: raw.fileId != null ? Number(raw.fileId) : undefined,
    fileSize: raw.size,
    sentAt: Date.now(),
    status: 'delivered',
  };
}

export function mapType(rawType?: string): MessageType {
  if (!rawType) return 'text';
  const t = rawType.toLowerCase();
  if (t.startsWith('image/') || t === 'photo') return 'photo';
  if (t.startsWith('video/') || t === 'video') return 'video';
  if (t.startsWith('audio/') || t === 'audio') return 'audio';
  if (t === 'document' || t === 'file') return 'document';
  return 'text';
}

function inferDirection(raw: RawIncomingMessage): 'in' | 'out' {
  if (raw.side === 'client') return 'out';
  if (raw.side) return 'in';
  const maybeType = typeof raw.type === 'number' ? raw.type : Number(raw.type);
  if (Number.isFinite(maybeType) && maybeType >= 10 && maybeType < 20) return 'out';
  return 'in';
}

function buildAuthor(raw: RawIncomingMessage): MessageAuthor | undefined {
  const u = raw.user;
  if (!u) return undefined;
  const name =
    [u.firstname, u.lastname].filter(Boolean).join(' ').trim() || u.name || 'Оператор';
  const initials = name
    .split(/\s+/)
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return { id: u.id, name, initials };
}

export function guessFileType(mime?: string): MessageType {
  if (!mime) return 'document';
  if (mime.startsWith('image/')) return 'photo';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

export function generateLocalId(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
